import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import PlotGridVisualizer from '../../components/bookings/PlotGridVisualizer';
import { CheckCircleIcon, MapPinIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { burialPlotBookingService } from '../../services/burial-plot-booking.service';
import { api } from '../../services/api';

interface Cemetery {
  id: string;
  name: string;
  assetName: string;
  providerId: string;
  providerName: string;
}

interface SelectedPlot {
  id: string;
  plotNumber: string;
  section: string;
  row: string;
  position: number;
  plotSize: 'ADULT' | 'CHILD';
  status: string;
  isAvailable: boolean;
  dimensions?: any;
  asset: {
    id: string;
    name: string;
    providerId: string;
  };
}

interface BurialData {
  burialSize: 'ADULT' | 'CHILD';
  preferredCemetery: string;
  preparationDate?: string;
  urgencyLevel: 'URGENT' | 'STANDARD';
  specialRequests?: string;
  areaCoordinates?: { lat: number; lng: number };
}

interface PricingResult {
  basePrice: number;
  urgencySurcharge: number;
  total: number;
  breakdown: string;
  currency?: string;
}

const BurialPlotSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCemetery, setSelectedCemetery] = useState<Cemetery | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedPlot, setSelectedPlot] = useState<SelectedPlot | null>(null);
  const [availableCemeteries, setAvailableCemeteries] = useState<Cemetery[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [sectionData, setSectionData] = useState<any>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Get data from navigation state
  const state = location.state as {
    burialData: BurialData;
    pricing: PricingResult;
    caseId?: string;
  };

  useEffect(() => {
    if (!state?.burialData) {
      setError('Missing booking information. Please start over.');
      setLoading(false);
      return;
    }

    loadCemeteries();
  }, [state]);

  useEffect(() => {
    if (selectedCemetery && state?.burialData) {
      loadSections();
    }
  }, [selectedCemetery, state]);

  useEffect(() => {
    if (selectedSection && selectedCemetery && state?.burialData) {
      loadSectionPlots();
    }
  }, [selectedSection, selectedCemetery, state]);

  const loadCemeteries = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get cemeteries with burial plots for the specified size
      const apiUrl = `/cemeteries/with-burial-plots?plotSize=${state!.burialData.burialSize}`;
      const response = await api.get(apiUrl);
      const data = response.data;

      if (data && Array.isArray(data)) {
        // Map cemetery data to our format
        const cemeteries: Cemetery[] = data.map((cemetery: any) => ({
          id: cemetery.id,
          name: cemetery.name,
          assetName: cemetery.assetName || cemetery.name,
          providerId: cemetery.providerId || cemetery.id,
          providerName: cemetery.providerName || cemetery.name
        }));

        // Filter to only include the selected cemetery if one was selected
        const filteredCemeteries = state!.burialData.preferredCemetery
          ? cemeteries.filter(c => state!.burialData.preferredCemetery === c.id)
          : cemeteries;

        setAvailableCemeteries(filteredCemeteries);

        // Auto-select first cemetery if none selected
        if (filteredCemeteries.length > 0 && !selectedCemetery) {
          setSelectedCemetery(filteredCemeteries[0]);
        }
      } else {
        throw new Error('No cemeteries found');
      }
    } catch (error) {
      console.error('Failed to load cemeteries:', error);
      setError('Failed to load available cemeteries');
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async () => {
    try {
      const sectionsData = await burialPlotBookingService.getCemeterySections(
        selectedCemetery!.id,
        state!.burialData.burialSize
      );
      setSections(sectionsData);

      // Auto-select first section if none selected
      if (sectionsData.length > 0 && !selectedSection) {
        setSelectedSection(sectionsData[0].name);
      }
    } catch (error) {
      console.error('Failed to load sections:', error);
      setError('Failed to load cemetery sections');
    }
  };

  const loadSectionPlots = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      }
      setError(null);

      const data = await burialPlotBookingService.getSectionPlots(
        selectedCemetery!.id,
        selectedSection!,
        state!.burialData.burialSize
      );

      setSectionData(data);
      setRetryCount(0);
    } catch (error) {
      console.error('Failed to load section plots:', error);
      setError('Failed to load plot data. Please try again.');
      setRetryCount(prev => prev + 1);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadSectionPlots(true);
  };

  const handlePlotSelect = (plot: any) => {
    if (plot) {
      // Add asset information to the selected plot
      const plotWithAsset: SelectedPlot = {
        ...plot,
        asset: {
          id: selectedCemetery!.id,
          name: selectedCemetery!.assetName,
          providerId: selectedCemetery!.providerId
        }
      };
      setSelectedPlot(plotWithAsset);
    } else {
      setSelectedPlot(null);
    }
  };

  const handleBack = () => {
    navigate('/bookings/burial-plot');
  };

  const handleContinue = () => {
    // Comprehensive validation before proceeding
    if (!selectedPlot) {
      toast.error('Please select an available plot to continue');
      return;
    }

    if (!selectedPlot.id) {
      toast.error('Invalid plot selection. Please select a different plot');
      return;
    }

    if (!selectedCemetery) {
      toast.error('No cemetery selected. Please select a cemetery first');
      return;
    }

    if (!state?.burialData) {
      toast.error('Missing booking information. Please start over');
      return;
    }

    // Create booking with selected plot
    createBooking();
  };

  const createBooking = async () => {
    try {
      setLoading(true);

      // Double-check all required data is available
      if (!selectedPlot || !selectedPlot.id) {
        toast.error('No plot selected. Please select a plot to continue.');
        return;
      }

      if (!selectedCemetery || !selectedCemetery.providerId) {
        toast.error('No cemetery selected. Please select a cemetery to continue.');
        return;
      }

      if (!state?.burialData) {
        toast.error('Missing booking data. Please start over.');
        return;
      }

      const bookingData = {
        providerId: selectedCemetery.providerId,
        burialSize: state.burialData.burialSize,
        burialArea: selectedCemetery.name, // Use selected cemetery name as burial area
        preparationDate: state.burialData.preparationDate ? new Date(state.burialData.preparationDate) : undefined,
        urgencyLevel: state.burialData.urgencyLevel,
        specialRequests: state.burialData.specialRequests,
        scheduledDateTime: state.burialData.preparationDate ? new Date(state.burialData.preparationDate) : new Date(),
        areaCoordinates: state.burialData.areaCoordinates,
        plotId: selectedPlot.id,
        caseId: state.caseId,
      };

      const bookingResponse = await burialPlotBookingService.createBooking(bookingData);

      if (bookingResponse && bookingResponse.success) {
        toast.success('Plot selected successfully! Proceeding to payment.');

        // Check if booking exists in data (handle nested structure)
        const bookingData = bookingResponse.data.data || bookingResponse.data;
        const booking = bookingData.booking || bookingData; // Handle both structures

        if (!booking || !booking.id) {
          toast.error('Invalid booking response received');
          return;
        }

        // Use a small timeout to ensure the toast is shown
        setTimeout(() => {
          navigate(`/bookings/burial-plot/payment/${booking.id}`, {
            state: {
              booking: booking,
              pricing: state.pricing,
              selectedPlot: selectedPlot
            }
          });
        }, 100);
      } else {
        toast.error(bookingResponse?.message || 'Failed to create booking');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to create burial plot booking');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total statistics
  const totalPlots = sectionData?.totalPlots || 0;
  const availablePlots = sectionData?.availablePlots || 0;
  const occupiedPlots = totalPlots - availablePlots;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (error && !selectedCemetery) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-red-400 mb-4">
              <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Cemeteries</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Booking Form
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                Select Your Burial Plot
              </h1>
              <p className="mt-2 text-sm text-neutral-600">
                Choose a specific plot at {selectedCemetery?.name} for your {state?.burialData.burialSize.toLowerCase()} burial.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Cemetery and Section Selection */}
          <div className="lg:col-span-1 space-y-4">
            {/* Cemetery Selection */}
            <div className="bg-white rounded-lg p-4 border border-neutral-200">
              <h3 className="font-medium text-gray-900 mb-3">Cemetery</h3>
              <div className="space-y-2">
                {availableCemeteries.map((cemetery) => (
                  <button
                    key={cemetery.id}
                    onClick={() => setSelectedCemetery(cemetery)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedCemetery?.id === cemetery.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-sm">{cemetery.name}</div>
                    <div className="text-xs text-gray-500">{cemetery.providerName}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Section Selection */}
            {sections.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-neutral-200">
                <h3 className="font-medium text-gray-900 mb-3">Section</h3>
                <div className="space-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.name}
                      onClick={() => setSelectedSection(section.name)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedSection === section.name
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{section.name}</div>
                      <div className="text-xs text-gray-500">
                        {section.availablePlots} of {section.totalPlots} available
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Plot Details */}
            {selectedPlot && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-green-900 mb-2">
                      Selected Plot
                    </div>
                    <div className="text-sm text-green-800 space-y-1">
                      <div>Plot: {selectedPlot.plotNumber}</div>
                      <div>Section: {selectedPlot.section}, Row {selectedPlot.row}</div>
                      <div>Size: {selectedPlot.plotSize}</div>
                      {selectedPlot.dimensions && typeof selectedPlot.dimensions === 'object' && (
                        <div>Dimensions: {selectedPlot.dimensions.length}m × {selectedPlot.dimensions.width}m</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content - Plot Grid */}
          <div className="lg:col-span-3">
            {sectionData && sectionData.rows ? (
              <div className="bg-white rounded-lg p-6 border border-neutral-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Section {selectedSection} - Plot Map
                    </h2>
                    <p className="text-gray-600 mt-1">
                      Click on an available plot to select it
                    </p>
                  </div>

                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>

                <PlotGridVisualizer
                  rows={sectionData.rows}
                  selectedPlotId={selectedPlot?.id || null}
                  onPlotSelect={handlePlotSelect}
                  readOnly={false}
                  showRowLabels={true}
                  showPlotNumbers={true}
                  gridSize="medium"
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg p-6 border border-neutral-200">
                <div className="text-center py-12">
                  <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                    <MapPinIcon />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Section</h3>
                  <p className="text-gray-500">
                    Choose a section from the left sidebar to view available plots
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between mt-6">
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back to Details
              </button>

              <button
                type="button"
                onClick={handleContinue}
                disabled={!selectedPlot || loading}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  selectedPlot && !loading
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue to Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BurialPlotSelectionPage;